import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { FaStar, FaSpinner, FaUser, FaBriefcase } from 'react-icons/fa';
import { SkeletonRatings } from '../components/Skeleton';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../services/api';
import SEOHead from '../components/SEOHead';

interface Rating {
  id: number;
  job_id: number;
  rated_user_id: number;
  rating_user_id: number;
  rating: number;
  comment: string;
  category: string;
  created_at: string;
  rater_name: string;
  rater_avatar: string;
}

interface RatingStats {
  average_rating: number;
  total_ratings: number;
  lowest_rating: number;
  highest_rating: number;
  positive_ratings: number;
  negative_ratings: number;
}

const RatingsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ratingsBoundary, setRatingsBoundary] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const requestSequence = useRef(0);
  const submissionSequence = useRef(0);
  const authBoundary = `${id || 'unknown'}:${
    isAuthenticated && user
      ? `${user.id}:${user.role}`
      : 'anonymous'
  }`;
  const currentBoundary = useRef(authBoundary);
  currentBoundary.current = authBoundary;
  const [newRating, setNewRating] = useState({
    rating: 5,
    comment: '',
    category: 'overall',
    jobId: '',
  });

  useEffect(() => {
    const requestId = ++requestSequence.current;
    submissionSequence.current += 1;
    const requestBoundary = authBoundary;
    const controller = new AbortController();

    setRatings([]);
    setStats(null);
    setRatingsBoundary(null);
    setLoading(true);
    setShowForm(false);
    setSubmitting(false);

    const fetchRatings = async () => {
      try {
        const res = await api.get(`/users/${id}/ratings`, {
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          requestSequence.current !== requestId ||
          currentBoundary.current !== requestBoundary
        ) {
          return;
        }
        setRatings(res.data.ratings || []);
        setStats(res.data.stats || null);
        setRatingsBoundary(requestBoundary);
      } catch (err) {
        if (
          !controller.signal.aborted &&
          requestSequence.current === requestId &&
          currentBoundary.current === requestBoundary
        ) {
          setRatingsBoundary(requestBoundary);
          toast.error('Failed to load ratings');
        }
      } finally {
        if (
          !controller.signal.aborted &&
          requestSequence.current === requestId &&
          currentBoundary.current === requestBoundary
        ) {
          setLoading(false);
        }
      }
    };

    fetchRatings();
    return () => controller.abort();
  }, [id, isAuthenticated, user?.id, user?.role, authBoundary, reloadVersion]);

  const handleSubmitRating = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRating.jobId) {
      toast.error('Please enter a job ID');
      return;
    }
    const submissionId = ++submissionSequence.current;
    const submissionBoundary = authBoundary;
    setSubmitting(true);
    try {
      await api.post('/ratings', {
        ratedUserId: Number(id),
        jobId: Number(newRating.jobId),
        rating: newRating.rating,
        comment: newRating.comment,
        category: newRating.category,
      });
      if (
        submissionSequence.current !== submissionId ||
        currentBoundary.current !== submissionBoundary
      ) {
        return;
      }
      toast.success('Review submitted!');
      setShowForm(false);
      setNewRating({ rating: 5, comment: '', category: 'overall', jobId: '' });
      setReloadVersion(version => version + 1);
    } catch (err) {
      if (
        submissionSequence.current === submissionId &&
        currentBoundary.current === submissionBoundary
      ) {
        const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
        toast.error(message || 'Failed to submit review');
      }
    } finally {
      if (
        submissionSequence.current === submissionId &&
        currentBoundary.current === submissionBoundary
      ) {
        setSubmitting(false);
      }
    }
  };

  const renderStars = (count: number, interactive = false) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map(star => (
          <FaStar
            key={star}
            className={`${
              star <= count ? 'text-yellow-500' : 'text-gray-300'
            } ${interactive ? 'cursor-pointer text-xl' : ''}`}
            onClick={interactive ? () => setNewRating(prev => ({ ...prev, rating: star })) : undefined}
          />
        ))}
      </div>
    );
  };

  const ratingsAreCurrent = ratingsBoundary === authBoundary;
  const canWriteReview = Boolean(isAuthenticated && user && user.id !== Number(id));

  if (loading || !ratingsAreCurrent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonRatings />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead title="Ratings & Reviews — StageCrew" description="View and manage your ratings and reviews on StageCrew." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold">Reviews</h1>
            <Link to="/profile/setup" className="text-blue-600 hover:underline">
              &larr; Back to Profile
            </Link>
          </div>
          {canWriteReview && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              {showForm ? 'Cancel' : 'Write a Review'}
            </button>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <div className="bg-white p-8 rounded shadow mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600">
                  {stats.average_rating?.toFixed(1) || '0.0'}
                </div>
                <div className="mt-2">{renderStars(Math.round(stats.average_rating || 0))}</div>
                <p className="text-sm text-gray-600 mt-1">Average Rating</p>
              </div>
              <div>
                <div className="text-4xl font-bold">{stats.total_ratings || 0}</div>
                <p className="text-sm text-gray-600 mt-1">Total Reviews</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-green-600">{stats.positive_ratings || 0}</div>
                <p className="text-sm text-gray-600 mt-1">Positive (4-5)</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-red-600">{stats.negative_ratings || 0}</div>
                <p className="text-sm text-gray-600 mt-1">Negative (1-2)</p>
              </div>
            </div>
          </div>
        )}

        {/* Review Form */}
        {showForm && canWriteReview && (
          <div className="bg-white p-8 rounded shadow mb-6">
            <h2 className="text-xl font-bold mb-4">Write a Review</h2>
            <form onSubmit={handleSubmitRating} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                {renderStars(newRating.rating, true)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job ID</label>
                <input
                  type="number"
                  value={newRating.jobId}
                  onChange={e => setNewRating(prev => ({ ...prev, jobId: e.target.value }))}
                  required
                  placeholder="Enter the job ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={newRating.category}
                  onChange={e => setNewRating(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="overall">Overall</option>
                  <option value="communication">Communication</option>
                  <option value="quality">Quality of Work</option>
                  <option value="timeliness">Timeliness</option>
                  <option value="professionalism">Professionalism</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Comment</label>
                <textarea
                  value={newRating.comment}
                  onChange={e => setNewRating(prev => ({ ...prev, comment: e.target.value }))}
                  rows={4}
                  placeholder="Share your experience..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {submitting ? <FaSpinner className="animate-spin mr-2" /> : null}
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        )}

        {/* Reviews List */}
        <div className="space-y-4">
          {ratings.length > 0 ? (
            ratings.map(rating => (
              <div key={rating.id} className="bg-white p-6 rounded shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm" role="img" aria-label={rating.rater_name || 'Anonymous user'}>
                      {rating.rater_name?.charAt(0) || <FaUser aria-hidden="true" />}
                    </div>
                    <div>
                      <p className="font-medium">{rating.rater_name || 'Anonymous'}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(rating.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {renderStars(rating.rating)}
                    {rating.category && (
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{rating.category}</span>
                    )}
                  </div>
                </div>
                {rating.comment && (
                  <p className="text-gray-700">{rating.comment}</p>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white p-12 rounded shadow text-center">
              <FaStar className="text-4xl text-gray-300 mx-auto mb-3" />
              <p className="text-gray-700 text-lg font-medium mb-1">No reviews yet</p>
              <p className="text-gray-500 text-sm mb-4">Complete gigs to start building your reputation and earning reviews.</p>
              <a
                href="/matches?status=matched"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
              >
                <FaBriefcase className="mr-2" aria-hidden="true" />
                View Matches
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatingsPage;
